/**
 * LinkTaskList
 * The tasks an external user's personal link opens, as admins see them: open
 * work orders and projects by title and building (the first few, then a
 * "Visa alla" toggle), and a count of the recently completed ones the guest
 * can still read. Order and selection come from the backend (the portal's).
 *
 * Shared by the assignment composer (PersonalLinkBlock) and Externa användare.
 */
import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { ExternalLinkTask } from '../types/guestAccess';

interface LinkTaskListProps {
    tasks: ExternalLinkTask[];
    /** Heading before the count, e.g. "Via länken idag" */
    heading: string;
    /** Shown when there are no open tasks; nothing is rendered when omitted */
    emptyText?: string;
    /** Open tasks shown before "Visa alla" */
    max?: number;
}

const LinkTaskList: React.FC<LinkTaskListProps> = ({ tasks, heading, emptyText, max = 5 }) => {
    const [showAll, setShowAll] = useState(false);
    const open = tasks.filter((t) => !t.completedAt);
    const completedCount = tasks.length - open.length;
    const shown = showAll ? open : open.slice(0, max);
    const hidden = open.length - shown.length;

    if (open.length === 0 && completedCount === 0) {
        return emptyText ? <div className="small text-muted">{emptyText}</div> : null;
    }

    return (
        <div className="small">
            {open.length > 0 ? (
                <>
                    <div className="fw-semibold">{`${heading} (${open.length}):`}</div>
                    <ul className="mb-1 ps-3">
                        {shown.map((t) => (
                            <li key={`${t.type}-${t.key}`}>
                                {t.title}
                                {t.type === 'project' && ' (projekt)'}
                                {t.buildingName && <span className="text-muted">{` – ${t.buildingName}`}</span>}
                            </li>
                        ))}
                    </ul>
                    {(hidden > 0 || showAll) && open.length > max && (
                        <Button variant="link" size="sm" className="p-0 mb-1" onClick={() => setShowAll((v) => !v)}>
                            {showAll ? 'Visa färre' : `och ${hidden} till – visa alla`}
                        </Button>
                    )}
                </>
            ) : (
                emptyText && <div className="text-muted">{emptyText}</div>
            )}
            {completedCount > 0 && (
                <div className="text-muted">
                    {`${completedCount === 1 ? 'En nyligen slutförd uppgift' : `${completedCount} nyligen slutförda uppgifter`} går att läsa i 30 dagar.`}
                </div>
            )}
        </div>
    );
};

export default LinkTaskList;
